import React, { useLayoutEffect, useState, useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import OtherFeesInput from "../../../shared/OtherFeesInput";
import { CustomLoading } from "@ippon/shared-components";
import { useForm } from "react-hook-form";
import {
  CustomModal,
  CustomButton,
  CustomTextInput,
  CustomTwoColInputGroup,
  CustomCalendarInput,
  CustomSingleSelect,
} from "@ippon/shared-components";
import {
  calculateContractMonthlyAmount,
  calculateTotalTsuboUnitPrice,
  formatDateForInput,
} from "@ippon/shared-utils";
import { Can } from "@/components/auth/Can";
import { useTsuboLinkedAmounts } from "@/hooks/useTsuboLinkedAmounts";

interface MakeContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyName: string;
  propertyAddress: string;
  onSubmit?: (data: MakeContractFormData) => void | Promise<void>;
  mode?: "create" | "edit";
  initialData?: Partial<MakeContractFormData>;
  isAncillary?: boolean;
  areaTsubo?: number;
  /** 既存顧客から登録する場合 true。契約者情報欄を編集不可 (グレーアウト) にする。 */
  lockContractorInfo?: boolean;
}

export type ContractorType = "individual" | "corporate";

export interface MakeContractFormData {
  propertyName: string;
  propertyAddress: string;
  contractorType: ContractorType;
  // 個人情報
  tenantLastName: string;
  tenantFirstName: string;
  tenantPhone: string;
  tenantEmail: string;
  // 法人情報
  companyName: string;
  representativeLastName: string;
  representativeFirstName: string;
  companyPhone: string;
  companyEmail: string;
  // 契約情報
  startDate: string;
  endDate: string;
  contract_form: string;
  rentStartDate: string;
  monthlyRent: string;
  rentUnitPrice: string;
  maintenanceFee: string;
  maintenanceFeeUnitPrice: string;
  totalRentUnitPrice: string;
  pricingType: "fixed" | "unit_quantity";
  unitPrice: string;
  quantity: string;
  quantityUnit: string;
  deposit: string;
  depositAfter: string;
  keyMoney: string;
  note: string;
}

const MakeContractModal: React.FC<MakeContractModalProps> = ({
  isOpen,
  onClose,
  propertyName,
  propertyAddress,
  onSubmit,
  mode = "create",
  initialData,
  isAncillary = false,
  areaTsubo,
  lockContractorInfo = false,
}) => {
  // 種別トグルは編集モードでは従来どおりロック。加えて既存顧客から登録する場合もロック。
  const typeSelectDisabled = mode === "edit" || lockContractorInfo;
  const [contractorType, setContractorType] =
    useState<ContractorType>("individual");
  const [otherFees, setOtherFees] = useState<
    Array<{ label: string; amount: string }>
  >([]);
  const [submitting, setSubmitting] = useState(false);

  const newContractForm = useForm<MakeContractFormData>({
    defaultValues: {
      propertyName,
      propertyAddress,
      contractorType: "individual",
      // 個人情報
      tenantLastName: "",
      tenantFirstName: "",
      tenantPhone: "",
      tenantEmail: "",
      // 法人情報
      companyName: "",
      representativeLastName: "",
      representativeFirstName: "",
      companyPhone: "",
      companyEmail: "",
      // 契約情報
      startDate: "",
      endDate: "",
      contract_form: "",
      rentStartDate: "",
      monthlyRent: "",
      rentUnitPrice: "",
      maintenanceFee: "",
      maintenanceFeeUnitPrice: "",
      totalRentUnitPrice: "",
      pricingType: "fixed",
      unitPrice: "",
      quantity: "1",
      quantityUnit: "台",
      deposit: "",
      depositAfter: "",
      keyMoney: "",
      note: "",
    },
  });

  // 契約者種別が変更された時に該当しないフィールドをクリア
  useEffect(() => {
    if (mode === "create") {
      // 新規作成モードのみクリア（編集モードはクリアしない）
      if (contractorType === "individual") {
        // 個人に切り替えた時、法人フィールドをクリア
        newContractForm.setValue("companyName", "");
        newContractForm.setValue("representativeLastName", "");
        newContractForm.setValue("representativeFirstName", "");
        newContractForm.setValue("companyPhone", "");
        newContractForm.setValue("companyEmail", "");
      } else if (contractorType === "corporate") {
        // 法人に切り替えた時、個人フィールドをクリア
        newContractForm.setValue("tenantLastName", "");
        newContractForm.setValue("tenantFirstName", "");
        newContractForm.setValue("tenantPhone", "");
        newContractForm.setValue("tenantEmail", "");
      }
    }
  }, [contractorType, mode, newContractForm]);

  useLayoutEffect(() => {
    if (isOpen) {
      if (mode === "edit" && initialData) {
        // 編集モード: initialDataを使用
        const type =
          (initialData.contractorType as ContractorType) || "individual";
        setContractorType(type);
        newContractForm.reset({
          propertyName,
          propertyAddress,
          contractorType: type,
          // 個人情報
          tenantLastName: initialData.tenantLastName || "",
          tenantFirstName: initialData.tenantFirstName || "",
          tenantPhone: initialData.tenantPhone || "",
          tenantEmail: initialData.tenantEmail || "",
          // 法人情報
          companyName: (initialData as any).companyName || "",
          representativeLastName:
            (initialData as any).representativeLastName || "",
          representativeFirstName:
            (initialData as any).representativeFirstName || "",
          companyPhone: (initialData as any).companyPhone || "",
          companyEmail: (initialData as any).companyEmail || "",
          // 契約情報
          startDate: initialData.startDate || "",
          endDate: initialData.endDate || "",
          contract_form: initialData.contract_form || "",
          rentStartDate: initialData.rentStartDate || "",
          monthlyRent: initialData.monthlyRent || "",
          rentUnitPrice: initialData.rentUnitPrice || "",
          maintenanceFee: initialData.maintenanceFee || "",
          maintenanceFeeUnitPrice: initialData.maintenanceFeeUnitPrice || "",
          pricingType: initialData.pricingType || "fixed",
          unitPrice: initialData.unitPrice || "",
          quantity: initialData.quantity || "1",
          quantityUnit: initialData.quantityUnit || "台",
          deposit: initialData.deposit || "",
          depositAfter: initialData.depositAfter || "",
          keyMoney: initialData.keyMoney || "",
          note: initialData.note || "",
        });
        setOtherFees((initialData as any).otherFees || []);
      } else {
        // 新規作成モード: デフォルト値。
        // 「既存顧客から」導線で initialData が渡された場合は契約者情報のみ反映
        // (契約条件の日付などはデフォルトのまま)。
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const presetType =
          (initialData?.contractorType as ContractorType) || "individual";
        setContractorType(presetType);
        newContractForm.reset({
          propertyName,
          propertyAddress,
          contractorType: presetType,
          // 個人情報
          tenantLastName: initialData?.tenantLastName || "",
          tenantFirstName: initialData?.tenantFirstName || "",
          tenantPhone: initialData?.tenantPhone || "",
          tenantEmail: initialData?.tenantEmail || "",
          // 法人情報
          companyName: (initialData as any)?.companyName || "",
          representativeLastName:
            (initialData as any)?.representativeLastName || "",
          representativeFirstName:
            (initialData as any)?.representativeFirstName || "",
          companyPhone: (initialData as any)?.companyPhone || "",
          companyEmail: (initialData as any)?.companyEmail || "",
          // 契約情報
          startDate: formatDateForInput(today),
          endDate: formatDateForInput(tomorrow),
          contract_form: "",
          rentStartDate: "",
          monthlyRent: "",
          rentUnitPrice: "",
          maintenanceFee: "",
          maintenanceFeeUnitPrice: "",
          pricingType: "fixed",
          unitPrice: "",
          quantity: "1",
          quantityUnit: "台",
          deposit: "",
          depositAfter: "",
          keyMoney: "",
          note: "",
        });
        setOtherFees([]);
      }
    }
  }, [isOpen, propertyName, propertyAddress, mode, initialData]);

  const handleClose = () => {
    newContractForm.reset();
    onClose();
  };

  useTsuboLinkedAmounts({
    form: newContractForm,
    areaTsubo,
    monthlyField: "monthlyRent",
    unitPriceField: "rentUnitPrice",
    enabled: !isAncillary,
  });
  useTsuboLinkedAmounts({
    form: newContractForm,
    areaTsubo,
    monthlyField: "maintenanceFee",
    unitPriceField: "maintenanceFeeUnitPrice",
    enabled: !isAncillary,
  });

  const totalUnitPrice = calculateTotalTsuboUnitPrice(
    Number(newContractForm.watch("rentUnitPrice")) || undefined,
    Number(newContractForm.watch("maintenanceFeeUnitPrice")) || undefined,
  );
  useEffect(() => {
    newContractForm.setValue(
      "totalRentUnitPrice",
      totalUnitPrice === undefined ? "" : String(totalUnitPrice),
      { shouldDirty: false, shouldValidate: false },
    );
  }, [newContractForm, totalUnitPrice]);
  const pricingType = newContractForm.watch("pricingType");
  const ancillaryUnitPrice = newContractForm.watch("unitPrice");
  const ancillaryQuantity = newContractForm.watch("quantity");
  useEffect(() => {
    if (!isAncillary || pricingType !== "unit_quantity") return;
    const monthlyAmount = calculateContractMonthlyAmount(
      ancillaryUnitPrice === "" ? undefined : Number(ancillaryUnitPrice),
      ancillaryQuantity === "" ? undefined : Number(ancillaryQuantity),
    );
    newContractForm.setValue(
      "monthlyRent",
      monthlyAmount === undefined ? "" : String(monthlyAmount),
      {
        shouldDirty:
          newContractForm.getFieldState("unitPrice").isDirty ||
          newContractForm.getFieldState("quantity").isDirty,
        shouldValidate: true,
      },
    );
  }, [
    ancillaryQuantity,
    ancillaryUnitPrice,
    isAncillary,
    newContractForm,
    pricingType,
  ]);

  const handleSubmit = newContractForm.handleSubmit(async (data) => {
    if (onSubmit) {
      const submitData = {
        ...data,
        contractorType,
        otherFees,
      };
      setSubmitting(true);
      try {
        await onSubmit(submitData);
        handleClose();
      } catch {
        // エラー時はモーダルを閉じない
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <CustomModal
      openModal={isOpen}
      handleCloseModal={handleClose}
      title={mode === "edit" ? "契約情報編集" : "新規契約"}
      modalType="create"
      addTitleBorder
      confirmOnDirtyClose={newContractForm.formState.isDirty}
    >
      <Box
        component="form"
        // CustomModal はポータルで body 直下に描画されるが、React の合成イベントは
        // DOM ではなく React ツリーを辿るため、submit がページ側の外側 <form>
        // (例: PropertyOverview) まで伝播して別トーストが二重発火する。境界で止める。
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(e);
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "80vh",
          maxHeight: "80vh",
          position: "relative",
          // CustomModal の .modalBoxWrapper が padding:16px を持つので、
          // このモーダルでは pb のみ打ち消す (= 下余白 0)。
          mb: "-16px",
        }}
      >
        {submitting && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.7)",
              borderRadius: "8px",
            }}
          >
            <CustomLoading loading={true} fullScreen={false} size="medium" />
          </Box>
        )}
        {/* スクロール可能なコンテンツエリア */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: { xs: "12px 12px 0 12px", sm: "12px 12px 0 12px" },
            // 既存顧客から登録時、disabled の契約者情報欄をグレーアウト
            // (前契約者情報モーダルと同じグレー)。
            // CustomTextInput 標準の Mui-disabled スタイルは関数値で当たらないため、
            // ここで disabled の入力欄に対して明示的に背景を当てる。
            ...(lockContractorInfo && {
              "& .MuiInputBase-root.Mui-disabled": {
                backgroundColor: "#f3f4f6",
              },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#3E3E3E",
                color: "#3E3E3E",
              },
            }),
          }}
        >
          <Stack spacing={0}>
            {/* 契約者種別 */}
            <Box sx={{ mb: 0 }}>
              <Typography fontSize={12} fontWeight={700} mb={0.5}>
                種別
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  p: 0.5,
                  mb: 1.5,
                  borderRadius: 3,
                  bgcolor: "#F1F3F5",
                  width: "auto",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 2.5,
                    top: 4,
                    bottom: 4,
                    width: "calc((100% - 16px) / 2)",
                    transform:
                      contractorType === "individual"
                        ? "translateX(0)"
                        : "translateX(calc(100% + 8px))",
                    transition: "transform 0.2s ease",
                    bgcolor: "#fff",
                    borderRadius: 2,
                    boxShadow: "0px 1px 2px rgba(16,24,40,0.08)",
                    border: "1px solid rgba(0,0,0,0.1)",
                    pointerEvents: "none",
                  }}
                />
                <Box
                  onClick={
                    !typeSelectDisabled
                      ? () => setContractorType("individual")
                      : undefined
                  }
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 4.5,
                    py: 1.25,
                    borderRadius: 2,
                    cursor: typeSelectDisabled ? "not-allowed" : "pointer",
                    bgcolor: "transparent",
                    color: contractorType === "individual" ? "#111" : "#6B7280",
                    border: "1px solid transparent",
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    個人
                  </Typography>
                </Box>
                <Box
                  onClick={
                    !typeSelectDisabled
                      ? () => setContractorType("corporate")
                      : undefined
                  }
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 4.5,
                    py: 1.25,
                    borderRadius: 2,
                    cursor: typeSelectDisabled ? "not-allowed" : "pointer",
                    bgcolor: "transparent",
                    color: contractorType === "corporate" ? "#111" : "#6B7280",
                    border: "1px solid transparent",
                    zIndex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    法人
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* 個人の場合 */}
            {contractorType === "individual" && (
              <>
                <CustomTwoColInputGroup
                  label="氏名"
                  firstName="tenantLastName"
                  lastName="tenantFirstName"
                  placeholderOne="氏"
                  placeholderTwo="名"
                  register={newContractForm.register as any}
                  errors={newContractForm.formState.errors as any}
                  isRequired={false}
                  disabled={lockContractorInfo}
                  marginBottom="0px"
                  inputWidth="100%"
                  inputWidthSp={"100%"}
                />
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="電話番号"
                    name="tenantPhone"
                    type="tel"
                    placeholder="例: 09012345678"
                    register={newContractForm.register}
                    errors={newContractForm.formState.errors}
                    isRequired={false}
                    disabled={lockContractorInfo}
                    inputWidth="100%"
                    inputMode="tel"
                  />
                </Box>
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="メールアドレス"
                    name="tenantEmail"
                    type="email"
                    placeholder="例: yamada@example.com"
                    register={newContractForm.register}
                    errors={newContractForm.formState.errors}
                    isRequired={false}
                    disabled={lockContractorInfo}
                    inputWidth="100%"
                  />
                </Box>
              </>
            )}

            {/* 法人の場合 */}
            {contractorType === "corporate" && (
              <>
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="法人名"
                    name="companyName"
                    placeholder="例: 株式会社○○"
                    register={newContractForm.register}
                    errors={newContractForm.formState.errors}
                    isRequired={false}
                    disabled={lockContractorInfo}
                    inputWidth="100%"
                  />
                </Box>
                <CustomTwoColInputGroup
                  label="代表者氏名"
                  firstName="representativeLastName"
                  lastName="representativeFirstName"
                  placeholderOne="氏"
                  placeholderTwo="名"
                  register={newContractForm.register as any}
                  errors={newContractForm.formState.errors as any}
                  isRequired={false}
                  disabled={lockContractorInfo}
                  marginBottom="0px"
                  inputWidth="100%"
                  inputWidthSp={"100%"}
                />
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="電話番号"
                    name="companyPhone"
                    type="tel"
                    placeholder="例: 0312345678"
                    register={newContractForm.register}
                    errors={newContractForm.formState.errors}
                    isRequired={false}
                    disabled={lockContractorInfo}
                    inputWidth="100%"
                    inputMode="tel"
                  />
                </Box>
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="メールアドレス"
                    name="companyEmail"
                    type="email"
                    placeholder="例: info@company.com"
                    register={newContractForm.register}
                    errors={newContractForm.formState.errors}
                    isRequired={false}
                    disabled={lockContractorInfo}
                    inputWidth="100%"
                  />
                </Box>
              </>
            )}
            <Box sx={{ mb: 0 }}>
              <CustomCalendarInput<MakeContractFormData>
                name="startDate"
                control={newContractForm.control as any}
                label="契約開始日"
                isRequired={false}
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomCalendarInput<MakeContractFormData>
                name="endDate"
                control={newContractForm.control as any}
                label="契約終了日"
                isRequired={false}
                rules={{
                  validate: (value) => {
                    const startDate = newContractForm.watch("startDate");
                    if (startDate && value) {
                      const start = new Date(startDate);
                      const end = new Date(value);
                      if (end <= start) {
                        return "契約終了日は契約開始日より後の日付である必要があります";
                      }
                    }
                    return true;
                  },
                }}
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomSingleSelect
                name="contract_form"
                control={newContractForm.control as any}
                label="契約形態"
                options={[
                  { value: "普通借", label: "普通借" },
                  { value: "定期借", label: "定期借" },
                  { value: "その他", label: "その他" },
                ]}
                isRequired={false}
                inputWidth="100%"
                inputWidthSp="100%"
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomCalendarInput<MakeContractFormData>
                name="rentStartDate"
                control={newContractForm.control as any}
                label="賃料起算日"
                isRequired={false}
              />
            </Box>
            {isAncillary && (
              <>
                <CustomSingleSelect
                  name="pricingType"
                  control={newContractForm.control}
                  label="料金体系"
                  options={[
                    { value: "fixed", label: "固定月額" },
                    { value: "unit_quantity", label: "単価 × 数量" },
                  ]}
                  inputWidth="100%"
                />
                {pricingType === "unit_quantity" && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 2,
                    }}
                  >
                    <CustomTextInput
                      label="単価（税別）"
                      name="unitPrice"
                      type="number"
                      showYen
                      register={newContractForm.register}
                      errors={newContractForm.formState.errors}
                      watch={newContractForm.watch}
                      setValue={newContractForm.setValue}
                      inputWidth="100%"
                    />
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) 120px",
                        gap: 1,
                      }}
                    >
                      <CustomTextInput
                        label="数量"
                        name="quantity"
                        type="number"
                        register={newContractForm.register}
                        errors={newContractForm.formState.errors}
                        inputWidth="100%"
                      />
                      <CustomSingleSelect
                        name="quantityUnit"
                        control={newContractForm.control}
                        label="単位"
                        options={[
                          { value: "台", label: "台" },
                          { value: "ヶ所", label: "ヶ所" },
                          { value: "基", label: "基" },
                          { value: "一式", label: "一式" },
                        ]}
                        inputWidth="100%"
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: isAncillary ? "1fr" : "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <CustomTextInput
                  label="月額賃料（税別）"
                  name="monthlyRent"
                  type="number"
                  showYen
                  watch={newContractForm.watch}
                  setValue={newContractForm.setValue}
                  placeholder="例: 150000"
                  register={newContractForm.register}
                  errors={newContractForm.formState.errors}
                  isRequired={false}
                  inputWidth="100%"
                  readOnly={isAncillary && pricingType === "unit_quantity"}
                />
              </Box>
              {!isAncillary && (
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="賃料坪単価"
                    name="rentUnitPrice"
                    type="number"
                    showYen
                    endAdornmentText="円/坪"
                    watch={newContractForm.watch}
                    setValue={newContractForm.setValue}
                    register={newContractForm.register}
                    errors={newContractForm.formState.errors}
                    disabled={!areaTsubo || areaTsubo <= 0}
                    inputWidth="100%"
                  />
                </Box>
              )}
            </Box>
            {!isAncillary && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <CustomTextInput
                      label="共益費"
                      name="maintenanceFee"
                      type="number"
                      showYen
                      watch={newContractForm.watch}
                      setValue={newContractForm.setValue}
                      placeholder="例: 10000"
                      register={newContractForm.register}
                      errors={newContractForm.formState.errors}
                      isRequired={false}
                      inputWidth="100%"
                    />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <CustomTextInput
                      label="共益費坪単価"
                      name="maintenanceFeeUnitPrice"
                      type="number"
                      showYen
                      endAdornmentText="円/坪"
                      watch={newContractForm.watch}
                      setValue={newContractForm.setValue}
                      register={newContractForm.register}
                      errors={newContractForm.formState.errors}
                      disabled={!areaTsubo || areaTsubo <= 0}
                      inputWidth="100%"
                    />
                  </Box>
                </Box>
                <Box sx={{ mb: 0 }}>
                  <CustomTextInput
                    label="賃料合計坪単価"
                    name="totalRentUnitPrice"
                    type="number"
                    showYen
                    endAdornmentText="円/坪"
                    watch={newContractForm.watch}
                    setValue={newContractForm.setValue}
                    register={newContractForm.register}
                    errors={newContractForm.formState.errors}
                    inputWidth="100%"
                    readOnly
                  />
                </Box>
              </>
            )}
            <Box sx={{ mb: 0 }}>
              <CustomTextInput
                label="敷金"
                name="deposit"
                type="number"
                showYen
                watch={newContractForm.watch}
                setValue={newContractForm.setValue}
                placeholder="例: 300000"
                register={newContractForm.register}
                errors={newContractForm.formState.errors}
                isRequired={false}
                inputWidth="100%"
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomTextInput
                label="償却後敷金"
                name="depositAfter"
                type="number"
                showYen
                watch={newContractForm.watch}
                setValue={newContractForm.setValue}
                placeholder="例: 100000"
                register={newContractForm.register}
                errors={newContractForm.formState.errors}
                isRequired={false}
                inputWidth="100%"
              />
            </Box>
            <Box sx={{ mb: 0 }}>
              <CustomTextInput
                label="礼金"
                name="keyMoney"
                type="number"
                showYen
                watch={newContractForm.watch}
                setValue={newContractForm.setValue}
                placeholder="例: 150000"
                register={newContractForm.register}
                errors={newContractForm.formState.errors}
                isRequired={false}
                inputWidth="100%"
              />
            </Box>
            {/* その他料金 */}
            <OtherFeesInput
              fees={otherFees}
              onChange={setOtherFees}
              inputWidth="100%"
            />
            <Box sx={{ mt: 2, mb: 0 }}>
              <CustomTextInput
                label="備考"
                name="note"
                placeholder="契約に関する特記事項があれば記入してください"
                register={newContractForm.register}
                errors={newContractForm.formState.errors}
                multiline={true}
                rows={3}
                inputWidth="100%"
              />
            </Box>
          </Stack>
        </Box>

        {/* 固定ボタンエリア */}
        <Box
          sx={{
            flexShrink: 0,
            py: "8px",
            borderTop: "1px solid #e5e7eb",
            bgcolor: "white",
          }}
        >
          <Stack direction="row" spacing={1} justifyContent="center">
            {/* <CustomButton
            label="キャンセル"
            buttonCategory="cancelModal"
            onClick={handleClose}
          /> */}
            <Can action="contract:edit">
              <CustomButton
                label={mode === "edit" ? "更新" : "新規契約"}
                buttonCategory="submit"
                type="submit"
                disabled={
                  submitting ||
                  (mode === "edit" && !newContractForm.formState.isDirty)
                }
              />
            </Can>
          </Stack>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default MakeContractModal;
